import React, { ElementType } from "react";
import clsx from "clsx";

type TypographyBaseProps<T = any> = T &
  React.PropsWithChildren<{
    className?: string;
  }>;

type HeadingProps<T extends ElementType> = React.ComponentPropsWithoutRef<T> &
  TypographyBaseProps<{
    italic?: boolean;
    strong?: boolean;
  }>;

export function Heading1({ children, italic, strong, className, ...rest }: HeadingProps<"h1">) {
  return (
    <h1
      className={clsx(
        "text-4xl",
        { italic: italic },
        strong ? "font-black" : "font-bold",
        className
      )}
      {...rest}
    >
      {children}
    </h1>
  );
}

export function Heading2({ children, italic, strong, className, ...rest }: HeadingProps<"h2">) {
  return (
    <h2
      className={clsx(
        "text-2xl",
        { italic: italic },
        strong ? "font-black" : "font-bold",
        className
      )}
      {...rest}
    >
      {children}
    </h2>
  );
}

export function Heading3({ children, italic, strong, className, ...rest }: HeadingProps<"h3">) {
  return (
    <h3
      className={clsx(
        "text-lg",
        { italic: italic },
        strong ? "font-black" : "font-bold",
        className
      )}
      {...rest}
    >
      {children}
    </h3>
  );
}

export function Heading4({ children, italic, strong, className, ...rest }: HeadingProps<"h4">) {
  return (
    <h4
      className={clsx(
        "text-base",
        { italic: italic },
        strong ? "font-black" : "font-bold",
        className
      )}
      {...rest}
    >
      {children}
    </h4>
  );
}

export function withBottomMargin<Props extends { className?: string }>(
  HeadingComponent: React.ComponentType<Props>
) {
  return function HeadingWithBottomMargin(props: Props) {
    return <HeadingComponent {...props} className={clsx("mb-1", props.className)} />;
  };
}

export function SuperNumber1({ children, className }: TypographyBaseProps) {
  return <span className={clsx("italic text-4xl font-black", className)}>{children}</span>;
}

export function SuperNumber2({ children, className }: TypographyBaseProps) {
  return <span className={clsx("italic text-2xl font-black", className)}>{children}</span>;
}

export function NoWrap({ children, className }: TypographyBaseProps) {
  return <span className={clsx("whitespace-nowrap", className)}>{children}</span>;
}
